import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Zap, CheckCircle2, XCircle, Loader2, ArrowRight, BookOpen, ExternalLink, RefreshCw, ChevronsRight, ScanLine, Library, Clock, ChevronDown, ChevronRight, Info, CalendarDays, List } from 'lucide-react';
import { ReviewQuestion, Language } from '../types';
import { loadReviewQuestions, recordQuestionAnswer, loadAllReviewQuestions } from '../services/reviewFeedService';

interface ReviewFeedPanelProps {
    userId: string;
    language: Language;
    activeNotebookId?: string;
    notebooks?: { id: string; nodes: { id: string; title: string }[] }[];
    onClose: () => void;
    onOpenNote: (nodeId: string, notebookId: string) => void;
    onGenerateMissing: () => void;
    isGeneratingMissing?: boolean;
}

type Phase = 'loading' | 'empty' | 'question' | 'answered';
type Tab = 'feed' | 'bank';
type BankView = 'by_note' | 'by_schedule';

// ── Helpers ───────────────────────────────────────────────────────────────────

const now = () => Date.now();
const HOUR = 3_600_000;
const DAY  = 86_400_000;

const getWhyReason = (q: ReviewQuestion, language: Language): string => {
    const vi = language === 'vietnamese';
    if (q.timesAsked === 0) {
        if (q.origin === 'active_review')
            return vi ? 'Mới tạo từ Memory Gym · nhắm vào điểm yếu của bạn' : 'New · targeted from your Memory Gym weakness';
        if (q.origin === 'initial_ai_check')
            return vi ? 'Mới tạo sau AI Check · chưa từng hỏi' : 'New · generated after AI Check';
        return vi ? 'Câu hỏi mới · chưa từng hỏi' : 'New question · never asked before';
    }
    const ratio = q.timesCorrect / q.timesAsked;
    if (ratio < 0.5)
        return vi ? 'Bạn đã trả lời sai câu này trước đó' : 'You answered this wrong before';
    return vi ? 'Đến hạn ôn lại' : 'Due for review';
};

const getScheduleStatus = (q: ReviewQuestion, language: Language): string => {
    const vi = language === 'vietnamese';
    const n = now();
    if (q.timesAsked === 0) return vi ? 'Chưa từng hỏi' : 'Never asked';
    const ratio = q.timesCorrect / q.timesAsked;
    if (!q.nextDueAt || q.nextDueAt <= n) {
        if (ratio < 0.5) return vi ? `Câu yếu · đúng ${q.timesCorrect}/${q.timesAsked}` : `Weak · ${q.timesCorrect}/${q.timesAsked} correct`;
        return vi ? `Đến hạn ôn · đúng ${q.timesCorrect}/${q.timesAsked}` : `Due · ${q.timesCorrect}/${q.timesAsked} correct`;
    }
    const diff = q.nextDueAt - n;
    const diffH = Math.round(diff / HOUR);
    const diffD = Math.round(diff / DAY);
    if (diffH < 1)  return vi ? 'Ôn lại trong ít phút' : 'Due very soon';
    if (diffH < 24) return vi ? `Ôn lại sau ${diffH} giờ` : `Review in ${diffH}h`;
    if (diffD === 1) return vi ? 'Ôn lại ngày mai' : 'Review tomorrow';
    return vi ? `Ôn lại sau ${diffD} ngày` : `Review in ${diffD} days`;
};

const computeNextDueLabel = (wasCorrect: boolean, newTimesCorrect: number, language: Language): string => {
    const vi = language === 'vietnamese';
    if (!wasCorrect) return vi ? 'Câu này sẽ quay lại sau 1 giờ.' : 'This will come back in 1 hour.';
    if (newTimesCorrect === 1) return vi ? 'Câu này sẽ quay lại ngày mai.' : 'This will come back tomorrow.';
    if (newTimesCorrect === 2) return vi ? 'Câu này sẽ quay lại sau 3 ngày.' : 'This will come back in 3 days.';
    return vi ? 'Câu này sẽ quay lại sau 7 ngày.' : 'This will come back in 7 days.';
};

// ── Bank sub-components ───────────────────────────────────────────────────────

const DueChip: React.FC<{ q: ReviewQuestion }> = ({ q }) => {
    const n = now();
    if (q.timesAsked === 0) return (
        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">NEW</span>
    );
    if (!q.nextDueAt || q.nextDueAt <= n) return (
        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">DUE</span>
    );
    const diffMs = q.nextDueAt - n;
    const diffH = Math.round(diffMs / HOUR);
    const diffD = Math.round(diffMs / DAY);
    const label = diffD >= 1 ? `${diffD}d` : `${diffH}h`;
    const ratio = q.timesAsked > 0 ? q.timesCorrect / q.timesAsked : 0;
    const colorClass = ratio >= 0.8
        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
        : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300';
    return <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${colorClass}`}>{label}</span>;
};

const OriginDot: React.FC<{ origin: string }> = ({ origin }) => {
    const colors: Record<string, string> = {
        initial_ai_check: 'bg-indigo-400',
        active_review: 'bg-green-400',
        manual_generation: 'bg-amber-400',
        source: 'bg-sky-400',
        wrong_answer: 'bg-red-400',
        regenerated: 'bg-purple-400',
    };
    return <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-0.5 ${colors[origin] ?? 'bg-gray-400'}`} />;
};

const QuestionRow: React.FC<{ q: ReviewQuestion; language: Language }> = ({ q, language }) => (
    <div className="flex items-start gap-2 px-5 py-2.5">
        <OriginDot origin={q.origin ?? q.questionOrigin ?? ''} />
        <div className="flex-1 min-w-0">
            <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-snug line-clamp-2">{q.question}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">{getScheduleStatus(q, language)}</p>
        </div>
        <DueChip q={q} />
    </div>
);

interface NoteGroup { nodeId: string; nodeTitle: string; notebookId: string; questions: ReviewQuestion[] }

const ByNoteView: React.FC<{ groups: NoteGroup[]; language: Language; onOpenNote: (nodeId: string, nbId: string) => void }> = ({ groups, language, onOpenNote }) => {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const n = now();
    return (
        <div className="divide-y dark:divide-slate-700/50">
            {groups.map(g => {
                const isOpen = expanded[g.nodeId] ?? false;
                const groupDue = g.questions.filter(q => !q.nextDueAt || q.nextDueAt <= n).length;
                return (
                    <div key={g.nodeId}>
                        <button
                            className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors text-left"
                            onClick={() => setExpanded(e => ({ ...e, [g.nodeId]: !isOpen }))}
                        >
                            {isOpen ? <ChevronDown size={13} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={13} className="text-gray-400 flex-shrink-0" />}
                            <span className="flex-1 text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">{g.nodeTitle}</span>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                {groupDue > 0 && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">{groupDue}</span>}
                                <span className="text-[10px] text-gray-400">{g.questions.length}Q</span>
                                <button onClick={e => { e.stopPropagation(); onOpenNote(g.nodeId, g.notebookId); }} className="p-0.5 text-blue-400 hover:text-blue-500 transition-colors">
                                    <ExternalLink size={10} />
                                </button>
                            </div>
                        </button>
                        {isOpen && (
                            <div className="bg-gray-50/50 dark:bg-slate-800/20 divide-y dark:divide-slate-700/30">
                                {g.questions.map(q => <QuestionRow key={q.id} q={q} language={language} />)}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const ByScheduleView: React.FC<{ questions: ReviewQuestion[]; language: Language }> = ({ questions, language }) => {
    const vi = language === 'vietnamese';
    const n = now();

    const buckets = useMemo(() => {
        const due    = questions.filter(q => q.timesAsked === 0 || !q.nextDueAt || q.nextDueAt <= n);
        const soon   = questions.filter(q => q.nextDueAt && q.nextDueAt > n && q.nextDueAt <= n + 4 * HOUR);
        const tmrw   = questions.filter(q => q.nextDueAt && q.nextDueAt > n + 4 * HOUR && q.nextDueAt <= n + 2 * DAY);
        const week   = questions.filter(q => q.nextDueAt && q.nextDueAt > n + 2 * DAY && q.nextDueAt <= n + 7 * DAY);
        const future = questions.filter(q => q.nextDueAt && q.nextDueAt > n + 7 * DAY);
        return [
            { label: vi ? 'Cần ôn ngay'   : 'Due now',      color: 'text-amber-600 dark:text-amber-400',   qs: due },
            { label: vi ? 'Hôm nay'        : 'Later today',  color: 'text-orange-500 dark:text-orange-400', qs: soon },
            { label: vi ? 'Ngày mai'       : 'Tomorrow',     color: 'text-blue-500 dark:text-blue-400',     qs: tmrw },
            { label: vi ? 'Tuần này'       : 'This week',    color: 'text-indigo-500 dark:text-indigo-400', qs: week },
            { label: vi ? 'Sau này'        : 'Future',       color: 'text-gray-400',                        qs: future },
        ].filter(b => b.qs.length > 0);
    }, [questions, vi]);

    return (
        <div className="divide-y dark:divide-slate-700/50">
            {buckets.map(b => (
                <div key={b.label}>
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-slate-800/40">
                        <span className={`text-[11px] font-bold ${b.color}`}>{b.label}</span>
                        <span className="text-[10px] text-gray-400">{b.qs.length}Q</span>
                    </div>
                    <div className="divide-y dark:divide-slate-700/30">
                        {b.qs.map(q => <QuestionRow key={q.id} q={q} language={language} />)}
                    </div>
                </div>
            ))}
        </div>
    );
};

const BankTab: React.FC<{ userId: string; language: Language; activeNotebookId?: string; notebooks?: { id: string; nodes: { id: string; title: string }[] }[]; onOpenNote: (nodeId: string, notebookId: string) => void }> = ({ userId, language, activeNotebookId, notebooks, onOpenNote }) => {
    const liveTitle = (nodeId: string, notebookId: string, fallback: string) =>
        notebooks?.find(nb => nb.id === notebookId)?.nodes.find(n => n.id === nodeId)?.title || fallback;
    const [questions, setQuestions] = useState<ReviewQuestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [bankView, setBankView] = useState<BankView>('by_note');
    const vi = language === 'vietnamese';

    useEffect(() => {
        setLoading(true);
        loadAllReviewQuestions(userId, activeNotebookId).then(qs => {
            setQuestions(qs);
            setLoading(false);
        });
    }, [userId, activeNotebookId]);

    if (loading) return (
        <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-amber-500" />
        </div>
    );

    if (questions.length === 0) return (
        <div className="flex flex-col items-center justify-center py-12 px-4 gap-3 text-center">
            <Library size={32} className="text-gray-300 dark:text-slate-600" />
            <p className="text-xs text-gray-400">{vi ? 'Chưa có câu hỏi nào trong ngân hàng.' : 'No questions in the bank yet.'}</p>
        </div>
    );

    const n = now();
    const totalDue = questions.filter(q => !q.nextDueAt || q.nextDueAt <= n).length;
    const totalNew = questions.filter(q => q.timesAsked === 0).length;

    // Build note groups for by_note view
    const groups: NoteGroup[] = [];
    const seen = new Map<string, NoteGroup>();
    for (const q of questions) {
        if (!seen.has(q.nodeId)) {
            const g = { nodeId: q.nodeId, nodeTitle: liveTitle(q.nodeId, q.notebookId, q.nodeTitle), notebookId: q.notebookId, questions: [] };
            seen.set(q.nodeId, g);
            groups.push(g);
        }
        seen.get(q.nodeId)!.questions.push(q);
    }

    return (
        <div className="flex flex-col">
            {/* Explainer */}
            <div className="flex items-start gap-2 px-4 py-3 border-b dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30">
                <Info size={12} className="text-gray-400 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-relaxed">
                    {vi
                        ? 'Feed ưu tiên câu chưa hỏi, câu đến hạn và câu bạn từng sai — theo lịch sử học của bạn.'
                        : 'Feed prioritizes new, due, and previously missed questions — based on your learning history.'}
                </p>
            </div>

            {/* Summary + view toggle */}
            <div className="flex items-center gap-3 px-4 py-2.5 border-b dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-400" />
                    <span className="text-[11px] text-gray-600 dark:text-gray-300">
                        <span className="font-bold">{totalDue}</span> {vi ? 'cần ôn' : 'due'}
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-[11px] text-gray-600 dark:text-gray-300">
                        <span className="font-bold">{totalNew}</span> {vi ? 'mới' : 'new'}
                    </span>
                </div>
                {/* View toggle */}
                <div className="ml-auto flex items-center gap-1 bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5">
                    <button
                        onClick={() => setBankView('by_note')}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors ${
                            bankView === 'by_note'
                                ? 'bg-white dark:bg-slate-600 text-gray-700 dark:text-gray-200 shadow-sm'
                                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                        }`}
                    >
                        <List size={9} /> {vi ? 'Ghi chú' : 'Notes'}
                    </button>
                    <button
                        onClick={() => setBankView('by_schedule')}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors ${
                            bankView === 'by_schedule'
                                ? 'bg-white dark:bg-slate-600 text-gray-700 dark:text-gray-200 shadow-sm'
                                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                        }`}
                    >
                        <CalendarDays size={9} /> {vi ? 'Lịch ôn' : 'Schedule'}
                    </button>
                </div>
            </div>

            {/* Content */}
            {bankView === 'by_note'
                ? <ByNoteView groups={groups} language={language} onOpenNote={onOpenNote} />
                : <ByScheduleView questions={questions} language={language} />
            }

            {/* Legend */}
            <div className="px-4 py-3 border-t dark:border-slate-700 mt-2">
                <p className="text-[10px] text-gray-400 font-semibold mb-1.5">{vi ? 'Nguồn câu hỏi' : 'Question origin'}</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {[
                        { color: 'bg-indigo-400', label: 'AI Check' },
                        { color: 'bg-green-400',  label: 'Memory Gym' },
                        { color: 'bg-sky-400',    label: vi ? 'Tài liệu gốc' : 'Source' },
                        { color: 'bg-amber-400',  label: vi ? 'Thủ công' : 'Manual' },
                    ].map(({ color, label }) => (
                        <div key={label} className="flex items-center gap-1">
                            <span className={`w-1.5 h-1.5 rounded-full ${color}`} />
                            <span className="text-[10px] text-gray-400">{label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ── Main Panel ────────────────────────────────────────────────────────────────

export const ReviewFeedPanel: React.FC<ReviewFeedPanelProps> = ({
    userId, language, activeNotebookId, notebooks, onClose, onOpenNote, onGenerateMissing, isGeneratingMissing = false,
}) => {
    const liveTitle = (nodeId: string, notebookId: string, fallback: string) =>
        notebooks?.find(nb => nb.id === notebookId)?.nodes.find(n => n.id === nodeId)?.title || fallback;
    const [tab, setTab] = useState<Tab>('feed');
    const [queue, setQueue] = useState<ReviewQuestion[]>([]);
    const [current, setCurrent] = useState<ReviewQuestion | null>(null);
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const [phase, setPhase] = useState<Phase>('loading');
    const [sessionAnswered, setSessionAnswered] = useState(0);
    const [sessionCorrect, setSessionCorrect] = useState(0);
    const [nextDueLabel, setNextDueLabel] = useState<string>('');

    const vi = language === 'vietnamese';

    const loadQueue = useCallback(async () => {
        setPhase('loading');
        const qs = await loadReviewQuestions(userId, activeNotebookId);
        if (qs.length === 0) {
            setCurrent(null);
            setQueue([]);
            setPhase('empty');
            return;
        }
        setCurrent(qs[0]);
        setQueue(qs.slice(1));
        setSelectedOption(null);
        setPhase('question');
    }, [userId, activeNotebookId]);

    useEffect(() => { loadQueue(); }, [loadQueue]);

    const handleAnswer = async (optionIdx: number) => {
        if (phase !== 'question' || !current) return;
        setSelectedOption(optionIdx);
        setPhase('answered');
        const correct = optionIdx === current.correctAnswerIndex;
        const newTimesCorrect = current.timesCorrect + (correct ? 1 : 0);
        setNextDueLabel(computeNextDueLabel(correct, newTimesCorrect, language));
        setSessionAnswered(s => s + 1);
        setSessionCorrect(s => s + (correct ? 1 : 0));
        await recordQuestionAnswer(userId, current.id, correct);
    };

    const handleNext = () => {
        if (queue.length === 0) { loadQueue(); return; }
        setCurrent(queue[0]);
        setQueue(q => q.slice(1));
        setSelectedOption(null);
        setPhase('question');
    };

    const handleSkip = () => {
        if (!current || queue.length === 0) return;
        setCurrent(queue[0]);
        setQueue(q => [...q.slice(1), current]);
        setSelectedOption(null);
        setPhase('question');
    };

    const scorePercent = sessionAnswered > 0 ? Math.round((sessionCorrect / sessionAnswered) * 100) : null;

    const l = {
        title: 'Review Feed',
        empty: vi ? 'Chưa có câu hỏi. Dùng AI Check trên ghi chú, hoặc nhấn nút bên dưới.' : 'No questions yet. Run AI Check on a note, or click the button below.',
        generateMissing: vi ? 'Tạo câu hỏi còn thiếu' : 'Generate Missing Questions',
        correct: vi ? 'Chính xác!' : 'Correct!',
        incorrect: vi ? 'Chưa đúng' : 'Incorrect',
        next: vi ? 'Tiếp theo' : 'Next',
        skip: vi ? 'Bỏ qua' : 'Skip',
        viewSource: vi ? 'Xem ghi chú gốc' : 'View source note',
        from: vi ? 'Từ:' : 'From:',
        remaining: vi ? 'còn lại' : 'left',
        reload: vi ? 'Tải lại' : 'Reload',
    };

    return (
        <div className="flex flex-col h-full bg-white dark:bg-slate-900 overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b dark:border-slate-700 flex-shrink-0 bg-white dark:bg-slate-900">
                <div className="flex items-center gap-2">
                    <Zap size={16} className="text-amber-500" />
                    <span className="font-bold text-sm text-gray-800 dark:text-white">{l.title}</span>
                    {tab === 'feed' && sessionAnswered > 0 && (
                        <span className="text-[11px] font-mono text-gray-400 ml-1">
                            {sessionCorrect}/{sessionAnswered}
                            {scorePercent !== null && (
                                <span className={`ml-1 font-bold ${scorePercent >= 80 ? 'text-green-500' : scorePercent >= 50 ? 'text-amber-500' : 'text-red-500'}`}>
                                    {scorePercent}%
                                </span>
                            )}
                        </span>
                    )}
                </div>
                <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors">
                    <X size={16} />
                </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b dark:border-slate-700 flex-shrink-0 bg-white dark:bg-slate-900">
                {([['feed', vi ? 'Ôn tập' : 'Feed', <Zap key="z" size={13} />], ['bank', vi ? 'Ngân hàng' : 'Bank', <Library key="l" size={13} />]] as const).map(([val, label, icon]) => (
                    <button
                        key={val}
                        onClick={() => setTab(val as Tab)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-colors border-b-2 ${tab === val ? 'border-amber-500 text-amber-600 dark:text-amber-400' : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'}`}
                    >
                        {icon}{label}
                    </button>
                ))}
            </div>

            {/* Generate Missing (Feed tab only) */}
            {tab === 'feed' && (
                <div className="px-3 py-2.5 border-b dark:border-slate-700 flex-shrink-0">
                    <button
                        onClick={onGenerateMissing}
                        disabled={isGeneratingMissing}
                        className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-xs font-bold rounded-lg border border-teal-200 dark:border-teal-800/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isGeneratingMissing
                            ? <><Loader2 size={13} className="animate-spin" />{vi ? 'Đang tạo...' : 'Generating...'}</>
                            : <><ScanLine size={13} />{l.generateMissing}</>
                        }
                    </button>
                </div>
            )}

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto">

                {/* ── Feed Tab ── */}
                {tab === 'feed' && (
                    <>
                        {phase === 'loading' && (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 size={28} className="animate-spin text-amber-500" />
                            </div>
                        )}

                        {phase === 'empty' && (
                            <div className="flex flex-col items-center justify-center py-12 px-4 gap-4 text-center">
                                <BookOpen size={36} className="text-gray-300 dark:text-slate-600" />
                                <p className="text-gray-500 dark:text-gray-400 text-xs leading-relaxed">{l.empty}</p>
                                <button onClick={loadQueue} className="text-xs text-amber-500 hover:text-amber-600 font-medium flex items-center gap-1">
                                    <RefreshCw size={12} /> {vi ? 'Thử lại' : 'Retry'}
                                </button>
                            </div>
                        )}

                        {(phase === 'question' || phase === 'answered') && current && (
                            <div className="flex flex-col gap-3 p-4">
                                {/* Source breadcrumb */}
                                <div className="flex items-center gap-1 text-[10px] text-gray-400 flex-wrap">
                                    <span>{l.from}</span>
                                    <button
                                        onClick={() => onOpenNote(current.nodeId, current.notebookId)}
                                        className="text-blue-500 hover:text-blue-600 hover:underline flex items-center gap-0.5 font-medium"
                                    >
                                        {liveTitle(current.nodeId, current.notebookId, current.nodeTitle || current.notebookTitle)}
                                        <ExternalLink size={9} />
                                    </button>
                                    {queue.length > 0 && (
                                        <span className="ml-auto text-gray-300 dark:text-slate-600">{queue.length} {l.remaining}</span>
                                    )}
                                </div>

                                {/* Why this question */}
                                <div className="flex items-center gap-1 text-[10px] text-gray-400 italic">
                                    <Info size={9} className="flex-shrink-0" />
                                    <span>{getWhyReason(current, language)}</span>
                                </div>

                                {/* Question */}
                                <p className="text-gray-800 dark:text-white font-semibold text-sm leading-relaxed">
                                    {current.question}
                                </p>

                                {/* Options */}
                                <div className="flex flex-col gap-2">
                                    {current.options.map((opt, idx) => {
                                        const isSelected = selectedOption === idx;
                                        const isCorrect = idx === current.correctAnswerIndex;
                                        let style = 'border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer';
                                        if (phase === 'answered') {
                                            if (isCorrect) style = 'border-green-400 bg-green-50 dark:bg-green-900/20 cursor-default';
                                            else if (isSelected) style = 'border-red-400 bg-red-50 dark:bg-red-900/20 cursor-default';
                                            else style = 'border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/30 opacity-40 cursor-default';
                                        }
                                        return (
                                            <button
                                                key={idx}
                                                disabled={phase === 'answered'}
                                                onClick={() => handleAnswer(idx)}
                                                className={`w-full text-left px-3 py-2.5 rounded-lg border-2 transition-all flex items-center gap-2 text-xs ${style}`}
                                            >
                                                <span className="font-bold text-gray-400 w-4 flex-shrink-0">{String.fromCharCode(65 + idx)}.</span>
                                                <span className="flex-1 text-gray-700 dark:text-gray-200 leading-snug">{opt}</span>
                                                {phase === 'answered' && isCorrect && <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" />}
                                                {phase === 'answered' && isSelected && !isCorrect && <XCircle size={14} className="text-red-500 flex-shrink-0" />}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Explanation + next review */}
                                {phase === 'answered' && (
                                    <div className={`rounded-lg px-3 py-2.5 text-xs leading-relaxed ${selectedOption === current.correctAnswerIndex ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                                        <p className={`font-bold mb-1 ${selectedOption === current.correctAnswerIndex ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                                            {selectedOption === current.correctAnswerIndex ? l.correct : l.incorrect}
                                        </p>
                                        <p className="text-gray-600 dark:text-gray-300">{current.explanation}</p>
                                        {nextDueLabel && (
                                            <p className="flex items-center gap-1 mt-2 text-[10px] text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-2">
                                                <Clock size={9} className="flex-shrink-0" />
                                                {nextDueLabel}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* ── Bank Tab ── */}
                {tab === 'bank' && (
                    <BankTab userId={userId} language={language} activeNotebookId={activeNotebookId} notebooks={notebooks} onOpenNote={onOpenNote} />
                )}
            </div>

            {/* Footer (Feed tab only) */}
            {tab === 'feed' && (phase === 'question' || phase === 'answered') && current && (
                <div className="flex-shrink-0 px-4 py-3 border-t dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-between gap-2">
                    {phase === 'answered' ? (
                        <button
                            onClick={() => onOpenNote(current.nodeId, current.notebookId)}
                            className="flex items-center gap-1 text-[11px] text-blue-500 hover:text-blue-600 font-medium"
                        >
                            <ExternalLink size={12} />
                            {l.viewSource}
                        </button>
                    ) : (
                        queue.length > 0 ? (
                            <button onClick={handleSkip} className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 font-medium transition-colors">
                                {l.skip} <ChevronsRight size={13} />
                            </button>
                        ) : <div />
                    )}

                    {phase === 'answered' && (
                        <button
                            onClick={handleNext}
                            className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold rounded-full transition-transform hover:scale-105 active:scale-95 ml-auto"
                        >
                            {queue.length === 0
                                ? <><RefreshCw size={13} />{l.reload}</>
                                : <>{l.next}<ArrowRight size={13} /></>
                            }
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
